import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { inr, compact } from "@/lib/format";
import { DaysToggle, Skeleton, SkuThumb } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import { Layers, Pencil, Trash2, Upload } from "lucide-react";
const GroupDialog = ({ open, onClose, editing, onSaved }) => {
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [skus, setSkus] = useState([]);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (open) {
      api.get("/tools/skus").then(({ data }) => setSkus(data.skus));
      setName(editing?.name || "");
      setSelected(editing?.skus || []);
      setFilter("");
    }
  }, [open, editing]);

  const toggle = (sku) => setSelected((s) => s.includes(sku) ? s.filter((x) => x !== sku) : [...s, sku]);

  const onUpload = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/tools/groups/parse-file", {
          name: f.name, mime: f.type || "application/octet-stream",
          data_base64: reader.result.split(",")[1],
        });
        if (data.count === 0) return toast.error("No SKU/ASIN values found in file");
        setSelected((s) => [...new Set([...s, ...data.skus])]);
        toast.success(`Added ${data.count} SKU/ASIN from ${f.name}`);
      } catch (err) {
        toast.error("Could not parse file");
      }
    };
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const save = async () => {
    if (!name.trim() || selected.length === 0) return toast.error("Name and at least one SKU required");
    if (editing?.id) await api.put(`/tools/groups/${editing.id}`, { name, skus: selected });
    else await api.post("/tools/groups", { name, skus: selected });
    toast.success("Group saved");
    onSaved();
    onClose();
  };

  const shown = skus.filter((s) => (s.sku + (s.title || "")).toLowerCase().includes(filter.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing?.id ? "Edit group" : "New product group"}</DialogTitle>
          <DialogDescription>Group products by SKU to track their combined sales.</DialogDescription>
        </DialogHeader>
        <Input placeholder="Group name (e.g. Footwear Range)" value={name} onChange={(e) => setName(e.target.value)} data-testid="group-name-input" />
        <div className="flex items-center gap-2">
          <Input placeholder="Search SKUs…" value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="group-sku-search" />
          <label className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-border cursor-pointer hover:border-blue-600 hover:text-blue-600 transition-colors" data-testid="group-upload-label">
            <Upload className="h-3.5 w-3.5" aria-hidden="true" /> Upload CSV / Excel
            <input type="file" accept=".csv,.xlsx,.xlsm,.txt" className="hidden" onChange={onUpload} data-testid="group-upload-input" />
          </label>
        </div>
        <div className="max-h-56 overflow-y-auto border border-border rounded-md divide-y divide-border">
          {shown.length === 0 && <p className="p-3 text-xs text-muted-foreground">No SKUs found yet — appears as order-items sync progresses.</p>}
          {shown.slice(0, 100).map((s) => (
            <label key={s.sku} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-muted/50">
              <input type="checkbox" checked={selected.includes(s.sku)} onChange={() => toggle(s.sku)} data-testid={`group-sku-${s.sku}`} />
              <span className="font-mono text-xs">{s.sku}</span>
              <span className="text-xs text-muted-foreground truncate flex-1">{s.title}</span>
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{selected.length} SKU(s) selected</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="group-save-button">Save group</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const istDate = (offsetDays = 0) =>
  new Date(Date.now() - offsetDays * 86400000).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const SkuBreakdownDialog = ({ group, qs, rangeLabel, onClose }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (group) {
      setRows(null);
      api.get(`/tools/groups/${group.id}/skus?${qs}`).then(({ data }) => setRows(data.rows));
    }
  }, [group, qs]);
  if (!group) return null;
  return (
    <Dialog open={!!group} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="group-sku-breakdown-dialog">
        <DialogHeader>
          <DialogTitle>{group.name} — SKU breakdown</DialogTitle>
          <DialogDescription>{rangeLabel} · sorted by revenue</DialogDescription>
        </DialogHeader>
        {!rows ? (
          <Skeleton className="h-64" />
        ) : (
          <div className="max-h-[60vh] overflow-y-auto border border-border rounded-md">
            <table className="w-full text-sm" data-testid="group-sku-breakdown-table">
              <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground sticky top-0">
                <tr>
                  <th className="px-3 py-2"></th>
                  <th className="text-left px-3 py-2 font-semibold">Product</th>
                  <th className="text-right px-3 py-2 font-semibold">Units</th>
                  <th className="text-right px-3 py-2 font-semibold">Revenue</th>
                  <th className="text-right px-3 py-2 font-semibold">ASP</th>
                  <th className="text-right px-3 py-2 font-semibold">Orders</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.sku} className={`border-t border-border ${r.units === 0 ? "opacity-50" : ""}`} data-testid={`breakdown-row-${r.sku}`}>
                    <td className="pl-3 py-1.5 w-12"><SkuThumb sku={r.sku} alt={r.title} size="h-8 w-8" /></td>
                    <td className="px-3 py-1.5">
                      {r.asin ? (
                        <a href={`https://www.amazon.in/dp/${r.asin}`} target="_blank" rel="noopener noreferrer"
                           className="group/link block" onClick={(e) => e.stopPropagation()}
                           title={`Open ${r.asin} on Amazon.in`} data-testid={`breakdown-link-${r.sku}`}>
                          <p className="max-w-[18rem] truncate text-xs font-medium group-hover/link:text-blue-600 group-hover/link:underline">{r.title || r.sku}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">{r.sku} · {r.asin} ↗</p>
                        </a>
                      ) : (
                        <>
                          <p className="max-w-[18rem] truncate text-xs font-medium" title={r.title}>{r.title || r.sku}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">{r.sku}</p>
                        </>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.units}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums font-semibold">{r.units ? inr(r.revenue) : "—"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.asp ? inr(r.asp) : "—"}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{r.orders}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const SalesTracker = () => {
  const [days, setDays] = useState(30);
  const [range, setRange] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drillGroup, setDrillGroup] = useState(null);

  const qs = range ? `start=${range.start}&end=${range.end}` : `days=${days}`;
  const yesterday = istDate(1);
  const isYesterday = range && range.start === yesterday && range.end === yesterday;
  const rangeLabel = range
    ? (isYesterday ? `Yesterday (${yesterday})` : `${range.start} → ${range.end}`)
    : `Last ${days} days`;

  const load = useCallback(() => {
    api.get(`/tools/groups?${qs}`).then(({ data }) => setData(data));
  }, [qs]);
  useEffect(() => { setData(null); load(); }, [load]);

  const applyCustom = (f, t) => {
    if (f && t && f <= t) setRange({ start: f, end: t });
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"?`)) return;
    await api.delete(`/tools/groups/${g.id}`);
    load();
  };

  if (!data) return <Skeleton className="h-64" />;

  return (
    <div className="space-y-5" data-testid="sales-tracker">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">Group products by SKU/ASIN and track their combined sales.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${isYesterday ? "bg-blue-600 text-white border-blue-600" : "border-border hover:border-blue-600 hover:text-blue-600"}`}
            onClick={() => setRange({ start: yesterday, end: yesterday })}
            data-testid="tracker-yesterday-button"
          >
            Yesterday
          </button>
          <DaysToggle value={range ? null : days} onChange={(d) => { setRange(null); setDays(d); }} />
          <div className="flex items-center gap-1.5">
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); applyCustom(e.target.value, to); }}
              className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="tracker-from-date" />
            <span className="text-xs text-muted-foreground">→</span>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); applyCustom(from, e.target.value); }}
              className="h-8 rounded-md border border-border bg-background px-2 text-xs" data-testid="tracker-to-date" />
          </div>
          <Button size="sm" className="rounded-full" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="new-group-button">+ New group</Button>
        </div>
      </div>
      {data.groups.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-10 text-center">
          <Layers className="h-8 w-8 mx-auto text-muted-foreground mb-3" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">No groups yet. Create one to track a category (e.g. "Footwear", "Electronics").</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.groups.map((g) => (
            <div key={g.id} className="bg-card border border-border rounded-lg p-5 shadow-sm cursor-pointer hover:border-blue-500/60 transition-colors" onClick={() => setDrillGroup(g)} data-testid={`group-card-${g.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-heading font-semibold">{g.name}</h3>
                  <p className="text-xs text-muted-foreground">{g.skus.length} SKUs · {rangeLabel.toLowerCase()} · click for SKU breakdown</p>
                </div>
                <div className="flex gap-1">
                  <button className="text-muted-foreground hover:text-foreground p-1" onClick={(e) => { e.stopPropagation(); setEditing(g); setDialogOpen(true); }} data-testid={`group-edit-${g.id}`} aria-label="Edit"><Pencil className="h-3.5 w-3.5" aria-hidden="true" /></button>
                  <button className="text-muted-foreground hover:text-red-600 p-1" onClick={(e) => { e.stopPropagation(); remove(g); }} data-testid={`group-delete-${g.id}`} aria-label="Delete"><Trash2 className="h-3.5 w-3.5" aria-hidden="true" /></button>
                </div>
              </div>
              <div className="flex gap-8 mt-4">
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Revenue</p><p className="font-heading text-xl font-bold">{compact(g.revenue)}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Units</p><p className="font-heading text-xl font-bold">{g.units}</p></div>
                <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Orders</p><p className="font-heading text-xl font-bold">{g.orders}</p></div>
              </div>
              <div className="h-20 mt-3">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={g.trend}>
                    <Tooltip formatter={(v, n) => [n === "revenue" ? inr(v) : v, n]} labelFormatter={(l, p) => p?.[0]?.payload?.date} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 11 }} />
                    <Area type="monotone" dataKey="revenue" stroke="#2563EB" fill="#2563EB" fillOpacity={0.25} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </div>
      )}
      <GroupDialog open={dialogOpen} onClose={() => setDialogOpen(false)} editing={editing} onSaved={load} />
      <SkuBreakdownDialog group={drillGroup} qs={qs} rangeLabel={rangeLabel} onClose={() => setDrillGroup(null)} />
    </div>
  );
};
